// Capacitor plugin name the native Swift/Kotlin bridge registers under.
// Mirrors the registerPlugin naming convention used by deviceTelemetry (Device, CapacitorUpdater).
export const NATIVE_CALLING_PLUGIN_NAME = 'NativeCalling';

// Events emitted by a telephony transport. Both the web and native implementations
// must emit these so downstream consumers (TelnyxProvider on web, the native plugin
// listener on native) are transport-agnostic.
export const TELEPHONY_TRANSPORT_EVENT = {
  // Connection/registration state of the underlying client (native plugin: TxClient socket + gateway
  // ready). Without this the native UI never knows it can place a call. Payload: { state, reason? } where
  // state is 'connected' | 'connecting' | 'disconnected' | 'error' and reason is a non-PII detail string
  // (gateway/connect-failure diagnostics — DEV-4332).
  CONNECTION_STATE_CHANGED: 'connectionStateChanged',
  INCOMING_CALL: 'incomingCall',
  CALL_ANSWERED: 'callAnswered',
  CALL_ENDED: 'callEnded',
  CALL_STATE_CHANGED: 'callStateChanged',
  // The native plugin emits the device's VoIP (PushKit) token so the JS layer can register it with
  // the backend device registry — without it the backend has no device to target for the inbound
  // ring-both push and incoming calls never ring. Payload: { token, platform } (platform: 'ios-voip').
  VOIP_PUSH_TOKEN: 'voipPushToken',
  // Non-PII iOS audio-session diagnostics for the cold-wake call-audio path (DEV-4332). The browser
  // App Insights SDK is disabled on native, so these are the only remote signal for whether CallKit
  // activated the audio session and whether the WebRTC audio device was enabled (no on-device Console
  // needed). Payload: { event, value?, reason?, callId } — event is one of 'answerIssued' |
  // 'didActivate' | 'didDeactivate' | 'audioDeviceEnabled'. No media is emitted on this channel.
  AUDIO_SESSION: 'audioSession',
  // Live WebRTC media-stats samples for the active call (DEV-4332 diagnostic). Unlike AUDIO_SESSION these
  // are forwarded LIVE (not via the buffered drain) so the App Insights timestamp is the REAL sample time —
  // needed to see exactly when/which direction inbound RTP stops. Payload: { callId, secs, stats } where
  // `stats` is a packed non-PII counter string (packets/bytes both directions + mos/rtt/jitter/levels).
  MEDIA_STATS: 'mediaStats',
  // Inbound-INVITE lifecycle (DEV-4332 diagnostic), forwarded LIVE (real timestamps) so the next on-device
  // test reads, per call: did iOS RECEIVE the bridge INVITE (phase 'inviteReceived'), what SIP did iOS send
  // (phase 'inviteResponse' → sipWeSent '200' accept vs 'none'/486 reject), and did it BRIDGE (phase
  // 'callOutcome'). THE disambiguation: a backend 486 for a callId with NO 'inviteReceived' from iOS came
  // from ANOTHER endpoint (the web session), not us. Payload: { phase, callId, telnyxCallId, ... } — all
  // non-PII (ids / CallKit uuid / flags / action / reason / sipWeSent / state). No caller name/number.
  CALL_LIFECYCLE: 'callLifecycle',
};

// Native-only signal (iOS cold-wake, DEV-4332): the native SDK finished registering the SIP credential
// while a VoIP-pushed inbound call is pending. NOT a cross-platform transport lifecycle event (no consumer
// subscribes to it) — it is handled entirely inside the native transport, which calls the telephony API so
// the backend dials an additional ring leg to this just-registered device. Payload: { callId } — the
// inbound call_id from the VoIP push.
export const NATIVE_CALLING_EVENT = {
  REGISTER_READY: 'registerReady',
};

// Connection states carried by CONNECTION_STATE_CHANGED.
export const TELEPHONY_CONNECTION_STATE = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
};

// App Insights custom-event name prefix for the native calling plugin's lifecycle (the browser SDK is
// disabled on native, so these are reported via the native telemetry reporter instead).
export const NATIVE_CALL_TELEMETRY_EVENT_PREFIX = 'NativeCalling.';

// ONLY non-PII fields are forwarded to App Insights — caller name/number from incomingCall must NEVER
// be sent as telemetry. Each transport event maps to the safe subset of its payload.
export const NATIVE_CALL_TELEMETRY_SAFE_PROPS = {
  // `reason` is the non-PII connect-failure detail (e.g. gateway-not-registered, readyTimeout, connected/
  // registered flags) — surfaced so a push-connect failure is diagnosable from App Insights (DEV-4332).
  // `pushTokenPresent` is the push-registration health signal: a `reged` state with pushTokenPresent=false
  // means we registered without a VoIP token, so Telnyx has nothing to push (the "no push when killed"
  // failure). The token value itself is a credential and is never emitted — only its presence (a boolean).
  [TELEPHONY_TRANSPORT_EVENT.CONNECTION_STATE_CHANGED]: ['state', 'reason', 'pushTokenPresent'],
  [TELEPHONY_TRANSPORT_EVENT.INCOMING_CALL]: ['callId'],
  [TELEPHONY_TRANSPORT_EVENT.CALL_ANSWERED]: ['callId'],
  [TELEPHONY_TRANSPORT_EVENT.CALL_ENDED]: ['callId', 'cause', 'sipCode'],
  [TELEPHONY_TRANSPORT_EVENT.CALL_STATE_CHANGED]: ['callId', 'telnyxCallState'],
  // The push token is a credential — NEVER forward it to telemetry; only the platform is safe.
  [TELEPHONY_TRANSPORT_EVENT.VOIP_PUSH_TOKEN]: ['platform'],
  // All audio-session diagnostic fields are non-PII (event label, bool value/flag, reason, callId).
  [TELEPHONY_TRANSPORT_EVENT.AUDIO_SESSION]: ['event', 'value', 'reason', 'callId'],
  // Media-stats sample fields are non-PII (callId, seconds-since-active, packed counter string).
  [TELEPHONY_TRANSPORT_EVENT.MEDIA_STATS]: ['callId', 'secs', 'stats'],
  // Inbound-INVITE lifecycle fields are all non-PII (phase label, ids, CallKit uuid, flags, action/reason,
  // the SIP we sent, bridge state). NEVER includes caller name/number.
  [TELEPHONY_TRANSPORT_EVENT.CALL_LIFECYCLE]: [
    'phase',
    'callId',
    'telnyxCallId',
    'boundToPushUuid',
    'wasPendingPush',
    'clientConnected',
    'clientRegistered',
    'action',
    'reason',
    'sipWeSent',
    'state',
    'telnyxState',
    'hadAudioUnit',
    'endReason',
  ],
};
